import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Grid,
  Button,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@mui/material';
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { format } from 'date-fns';
import { InterviewSchedule } from '../../types/interview';
import api from '../../utils/api';
import { getCounsellorStatus, getStatusChipStyles } from '../../utils/counsellorUtils';
import { useAuth } from '../../context/AuthContext';
import { styled } from '@mui/material';


const StyledDatePicker = styled('div')(({ theme }) => ({
  position: 'relative',
  '& .react-datepicker-wrapper': {
    width: '100%',
  },
  '& .react-datepicker__input-container input': {
    width: '100%',
    padding: '8px 12px',
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
    fontSize: '1rem',
    backgroundColor: '#fff',
    '&:focus': {
      outline: 'none',
      borderColor: theme.palette.primary.main,
    },
  },
  '& .react-datepicker': {
    backgroundColor: '#fff !important',
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
    fontFamily: theme.typography.fontFamily,
    zIndex: 9999,
  },
  '& .react-datepicker__header': {
    backgroundColor: '#f5f5f5 !important',
    borderBottom: `1px solid ${theme.palette.divider}`,
  },
  '& .react-datepicker__time-container': {
    borderLeft: `1px solid ${theme.palette.divider}`,
    backgroundColor: '#fff !important',
  },
  '& .react-datepicker__time': {
    backgroundColor: '#fff !important',
  },
  '& .react-datepicker__time-box': {
    backgroundColor: '#fff !important',
  },
  '& .react-datepicker-popper': {
    zIndex: 9999,
    position: 'fixed !important',
  },
  '& .react-datepicker__day--selected': {
    backgroundColor: `${theme.palette.primary.main} !important`,
    color: '#fff',
  },
  '& .react-datepicker__time-list-item--selected': {
    backgroundColor: `${theme.palette.primary.main} !important`,
    color: '#fff !important',
  },
}));

const InterviewManagement = () => {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [interviews, setInterviews] = useState<InterviewSchedule[]>([]);
  const [selectedInterview, setSelectedInterview] = useState<InterviewSchedule | null>(null);
  const [openRescheduleDialog, setOpenRescheduleDialog] = useState(false);
  const [rescheduleDate, setRescheduleDate] = useState<Date | null>(null);
  const [rescheduleReason, setRescheduleReason] = useState('');
  const [openCancelDialog, setOpenCancelDialog] = useState(false);
  const [cancelReason, setCancelReason] = useState('');
  const [, setLoading] = useState(false);
  const [, setError] = useState<string | null>(null);

  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage] = useState(10);

  const indexOfLastItem = currentPage * rowsPerPage;
  const indexOfFirstItem = indexOfLastItem - rowsPerPage;


  const filteredInterviews = interviews.filter(interview => {
    const matchesSearch = searchQuery === '' || 
      `${interview.counsellor.firstName} ${interview.counsellor.lastName}`
        .toLowerCase()
        .includes(searchQuery.toLowerCase());

    const matchesFilter = statusFilter === 'all' || 
      interview.interview.status.toLowerCase() === statusFilter.toLowerCase();

    return matchesSearch && matchesFilter;
  });

  const getPaginatedData = () => {
    const startIndex = (currentPage - 1) * rowsPerPage;
    const endIndex = startIndex + rowsPerPage;
    return {
      paginatedData: filteredInterviews.slice(startIndex, endIndex),
      totalCount: filteredInterviews.length
    };
  };

  const handlePrevPage = () => {
    setCurrentPage(prev => Math.max(prev - 1, 1));
  };

  const handleNextPage = () => {
    setCurrentPage(prev => Math.min(prev + 1, Math.ceil(filteredInterviews.length / rowsPerPage)));
  };


  const { user, isAuthenticated } = useAuth();

  const fetchInterviews = async () => {
    try {
      setError(null);
      
      // Fetch only from the interviews API - this is the single source of truth
      console.log('Fetching interviews...');
      const response = await api.get('/api/interviews');
      
      console.log('Interviews response:', response.data);
      
      // Sort by scheduled date (most recent first)
      const sortedInterviews = response.data.sort((a: InterviewSchedule, b: InterviewSchedule) => 
        new Date(b.interview.scheduledDate).getTime() - 
        new Date(a.interview.scheduledDate).getTime()
      );

      setInterviews(sortedInterviews);
    } catch (error: any) {
      console.error('Error in fetchInterviews:', error);
      setError(error.response?.data?.message || error.message || 'Failed to fetch interviews');
    }
  };

  // Initial load with loading state
  useEffect(() => {
    // Check if user is authenticated and is admin
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }
    
    if (!user || user.role !== 'administrator') {
      console.log('Access denied - Required role: administrator, User role:', user?.role);
      navigate('/dashboard');
      return;
    }

    const loadInitialData = async () => {
      setLoading(true);
      await fetchInterviews();
      setLoading(false);
    };
    loadInitialData();
  }, [isAuthenticated, user, navigate]);

  // Auto-refresh every 30 seconds (silent, no loading state)
  useEffect(() => {
    if (!isAuthenticated || user?.role !== 'administrator') return;

    const refreshInterval = setInterval(() => {
      fetchInterviews();
    }, 30000);

    return () => clearInterval(refreshInterval);
  }, [isAuthenticated, user]);

  const handleReschedule = async () => {
    if (!selectedInterview || !rescheduleDate) return;
    
    // Validate that reschedule date is in the future
    const now = new Date();
    if (rescheduleDate <= now) {
      setError('Reschedule date must be in the future');
      return;
    }

    try {
      console.log('Rescheduling interview:', {
        interviewId: selectedInterview.interview.id,
        newDate: rescheduleDate
      });

      // Update the interview with new date and status
      await api.put(`/api/interviews/${selectedInterview.interview.id}`, {
        scheduledDate: rescheduleDate.toISOString(),
        status: 'rescheduled',
        notes: rescheduleReason ? `Rescheduled: ${rescheduleReason}` : selectedInterview.interview.notes
      });

      setOpenRescheduleDialog(false);
      setRescheduleDate(null);
      setRescheduleReason('');
      setError('');
      
      // Refresh the interviews list
      await fetchInterviews();
    } catch (error: any) {
      console.error('Error rescheduling interview:', error);
      setError(error.response?.data?.message || error.message || 'Failed to reschedule interview');
    }
  };

  const handleCancel = async () => {
    if (!selectedInterview) return;

    try {
      // Update the interview status to cancelled
      await api.put(`/api/interviews/${selectedInterview.interview.id}`, {
        status: 'cancelled',
        notes: cancelReason ? `Cancelled: ${cancelReason}` : selectedInterview.interview.notes
      });
      
      await fetchInterviews();
      setOpenCancelDialog(false);
      setCancelReason('');
    } catch (error: any) {
      console.error('Error canceling interview:', error);
      setError(error.response?.data?.message || error.message || 'Failed to cancel interview');
    }
  };

  const handleOpenRescheduleDialog = (interview: InterviewSchedule) => {
    setSelectedInterview(interview);
    setRescheduleDate(new Date(interview.interview.scheduledDate));
    setRescheduleReason('');
    setOpenRescheduleDialog(true);
  };


  const handleOpenCancelDialog = (interview: InterviewSchedule) => {
    setSelectedInterview(interview);
    setCancelReason('');
    setOpenCancelDialog(true);
  };


  return (
    <>
      {/* Info Banner */}
      <div className="mb-6 p-4 glass-card-light border-l-4 border-blue-400">
        <Typography variant="body1" className="theme-text-primary mb-2 font-semibold">
          Interview Management
        </Typography>
        <Typography variant="body2" className="theme-text-secondary">
          Schedule, manage, and track counsellor interviews. Interviews automatically create Google Meet links.
        </Typography>
      </div>

      {/* Search and Filter Section */}
          <div className="mt-8 mb-6">
            <Grid container spacing={3}>
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  label="Search"
                  variant="outlined"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="theme-input"
                  fullWidth
                  sx={{
                    '& .MuiInputLabel-root': {
                      color: 'rgba(255, 255, 255, 0.7)'
                    },
                    '& .MuiInputLabel-root.Mui-focused': {
                      color: 'rgba(255, 255, 255, 0.9)'
                    },
                    '& .MuiOutlinedInput-input': {
                      color: 'white'
                    }
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <FormControl fullWidth className="theme-input"
                  sx={{
                    '& .MuiInputLabel-root': {
                      color: 'rgba(255, 255, 255, 0.7)'
                    },
                    '& .MuiInputLabel-root.Mui-focused': {
                      color: 'rgba(255, 255, 255, 0.9)'
                    },
                    '& .MuiSelect-select': {
                      color: 'white'
                    }
                  }}
                >
                  <InputLabel>Filter by Status</InputLabel>
                  <Select
                    value={statusFilter}
                    label="Filter by Status"
                    onChange={(e) => setStatusFilter(e.target.value)}
                  >
                    <MenuItem value="all">All</MenuItem>
                    <MenuItem value="scheduled">Scheduled</MenuItem>
                    <MenuItem value="completed">Completed</MenuItem>
                    <MenuItem value="cancelled">Cancelled</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          </div>

          {/* Interview Table */}
          <div className="overflow-x-auto glass-card rounded-lg">
            <table className="min-w-full">
              <thead className="glass-card-light">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-bold theme-text-primary uppercase tracking-wider">
                    Counsellor Name
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-bold theme-text-primary uppercase tracking-wider">
                    Email
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-bold theme-text-primary uppercase tracking-wider">
                    Interview Date
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-bold theme-text-primary uppercase tracking-wider">
                    Google Meet
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-bold theme-text-primary uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-bold theme-text-primary uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-white/10">
                {getPaginatedData().paginatedData.map((interview) => (
                  <tr key={interview.counsellor.id} className="hover:bg-white/5 transition-colors">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium theme-text-primary">
                        {interview.counsellor.firstName} {interview.counsellor.lastName}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm theme-text-secondary">
                        {interview.counsellor.email}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm theme-text-secondary">
                        {interview.interview.scheduledDate
                          ? format(new Date(interview.interview.scheduledDate), 'dd/MM/yyyy HH:mm')
                          : 'Not scheduled'}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm">
                        {interview.interview.meetingLink ? (
                          <a
                            href={interview.interview.meetingLink}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-blue-400 hover:text-blue-300 font-medium transition-colors"
                          >
                            Join Meeting
                          </a>
                        ) : (
                          <span className="text-gray-500">Not available</span>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm">
                        <Chip
                          label={getCounsellorStatus({
                            applicationStatus: interview.interview.status === 'completed' ? 'Interview Completed' : 'Interview Scheduled'
                          })}
                          sx={{
                            height: '24px',
                            borderRadius: '12px',
                            fontSize: '0.75rem',
                            ...getStatusChipStyles({
                              applicationStatus: interview.interview.status === 'completed' ? 'Interview Completed' : 'Interview Scheduled'
                            })
                          }}
                        />
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <button
                        onClick={() => handleOpenRescheduleDialog(interview)}
                        className="text-blue-400 font-bold hover:text-blue-300 mr-4 transition-colors"
                      >
                        Reschedule
                      </button>
                      <button
                        onClick={() => handleOpenCancelDialog(interview)}
                        className="text-red-400 font-bold hover:text-red-300 transition-colors"
                      >
                        Cancel
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          <div className="flex justify-between items-center mt-4 px-6 py-3 glass-card-light border-t border-white/10">
            <div className="flex-1 flex justify-between sm:hidden">
              <button
                onClick={handlePrevPage}
                disabled={currentPage === 1}
                className={`relative inline-flex items-center px-4 py-2 border border-white/20 text-sm font-medium rounded-md ${
                  currentPage === 1
                    ? 'bg-white/5 text-white/40'
                    : 'bg-white/10 theme-text-primary hover:bg-white/20'
                }`}
              >
                Previous
              </button>
              <button
                onClick={handleNextPage}
                disabled={currentPage === Math.ceil(filteredInterviews.length / rowsPerPage)}
                className={`ml-3 relative inline-flex items-center px-4 py-2 border border-white/20 text-sm font-medium rounded-md ${
                  currentPage === Math.ceil(filteredInterviews.length / rowsPerPage)
                    ? 'bg-white/5 text-white/40'
                    : 'bg-white/10 theme-text-primary hover:bg-white/20'
                }`}
              >
                Next
              </button>
            </div>
            <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
              <div>
                <p className="text-sm theme-text-secondary">
                  Showing{' '}
                  <span className="font-medium">{indexOfFirstItem + 1}</span>
                  {' - '}
                  <span className="font-medium">
                    {Math.min(indexOfLastItem, filteredInterviews.length)}
                  </span>{' '}
                  of <span className="font-medium">{filteredInterviews.length}</span> results
                </p>
              </div>
              <div>
                <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px">
                  <button
                    onClick={handlePrevPage}
                    disabled={currentPage === 1}
                    className={`relative inline-flex items-center px-2 py-2 rounded-l-md border border-white/20 bg-white/10 text-sm font-medium ${
                      currentPage === 1
                        ? 'text-white/40'
                        : 'theme-text-primary hover:bg-white/20'
                    }`}
                  >
                    Previous
                  </button>
                  <button
                    onClick={handleNextPage}
                    disabled={currentPage === Math.ceil(filteredInterviews.length / rowsPerPage)}
                    className={`relative inline-flex items-center px-2 py-2 rounded-r-md border border-white/20 bg-white/10 text-sm font-medium ${
                      currentPage === Math.ceil(filteredInterviews.length / rowsPerPage)
                        ? 'text-white/40'
                        : 'theme-text-primary hover:bg-white/20'
                    }`}
                  >
                    Next
                  </button>
                </nav>
              </div>
            </div>
          </div>

          <Dialog 
            open={openRescheduleDialog} 
            onClose={() => setOpenRescheduleDialog(false)} 
            maxWidth="sm" 
            fullWidth
            sx={{
              zIndex: 1300,
            }}
            PaperProps={{
              sx: {
                backgroundColor: 'rgba(30, 41, 59, 0.95)',
                backdropFilter: 'blur(20px)',
                border: '1px solid rgba(255, 255, 255, 0.1)',
                boxShadow: '0 8px 32px 0 rgba(0, 0, 0, 0.37)',
                zIndex: 1300,
                overflow: 'visible',
                '& .MuiDialogContent-root': {
                  overflow: 'visible',
                },
              }
            }}
          >
            <DialogTitle sx={{ fontWeight: 'bold', color: 'rgba(255, 255, 255, 0.9)', pb: 1 }}>
              Reschedule Interview
              {selectedInterview && (
                <Typography variant="subtitle2" sx={{ mt: 1, color: 'rgba(255, 255, 255, 0.7)' }}>
                  {selectedInterview.counsellor.firstName} {selectedInterview.counsellor.lastName}
                </Typography>
              )}
            </DialogTitle>
            <DialogContent sx={{ overflow: 'visible' }}>
              <Box sx={{ mt: 2 }}>
                <Typography variant="subtitle2" sx={{ mb: 1, color: 'rgba(255, 255, 255, 0.9)', fontWeight: 500 }}>
                  Select New Interview Date and Time
                </Typography>
                <StyledDatePicker>
                  <DatePicker
                    selected={rescheduleDate}
                    onChange={(date: Date | null) => setRescheduleDate(date)}
                    showTimeSelect
                    timeFormat="HH:mm"
                    timeIntervals={30}
                    dateFormat="dd/MM/yyyy HH:mm"
                    placeholderText="Select date and time"
                    minDate={new Date()}
                    timeCaption="Time"
                  />
                </StyledDatePicker>
                <Box sx={{ mt: 3 }}>
                  <Typography variant="subtitle2" sx={{ mb: 1, color: 'rgba(255, 255, 255, 0.9)', fontWeight: 500 }}>
                    Reason for Rescheduling
                  </Typography>
                  <TextField
                    fullWidth
                    multiline
                    rows={3}
                    value={rescheduleReason}
                    onChange={(e) => setRescheduleReason(e.target.value)}
                    placeholder="Enter reason for rescheduling"
                    variant="outlined"
                    sx={{
                      '& .MuiInputBase-input': { color: 'white' },
                      '& .MuiInputLabel-root': { color: 'rgba(255, 255, 255, 0.7)' },
                      '& .MuiOutlinedInput-root': {
                        '& fieldset': { borderColor: 'rgba(255, 255, 255, 0.3)' },
                        '&:hover fieldset': { borderColor: 'rgba(255, 255, 255, 0.5)' }
                      }
                    }}
                  />
                </Box>
              </Box>
            </DialogContent>
            <DialogActions sx={{ p: 3, pt: 1 }}>
              <Button 
                onClick={() => setOpenRescheduleDialog(false)} 
                sx={{ 
                  color: 'rgba(255, 255, 255, 0.9)',
                  borderColor: 'rgba(255, 255, 255, 0.3)',
                  '&:hover': {
                    borderColor: 'rgba(255, 255, 255, 0.5)',
                    backgroundColor: 'rgba(255, 255, 255, 0.05)'
                  }
                }}
              >
                Cancel
              </Button>
              <Button
                onClick={handleReschedule}
                variant="contained"
                disabled={!rescheduleDate || !rescheduleReason}
                sx={{
                  backgroundColor: 'rgba(76, 175, 80, 0.2)',
                  color: 'rgba(255, 255, 255, 0.9)',
                  border: '1px solid rgba(76, 175, 80, 0.3)',
                  '&:hover': {
                    backgroundColor: 'rgba(76, 175, 80, 0.3)',
                    borderColor: 'rgba(76, 175, 80, 0.5)'
                  },
                  '&:disabled': {
                    backgroundColor: 'rgba(255, 255, 255, 0.1)',
                    color: 'rgba(255, 255, 255, 0.3)',
                    borderColor: 'rgba(255, 255, 255, 0.1)'
                  }
                }}
              >
                Reschedule Interview
              </Button>
            </DialogActions>
          </Dialog>

          <Dialog 
            open={openCancelDialog} 
            onClose={() => setOpenCancelDialog(false)} 
            maxWidth="sm" 
            fullWidth
            sx={{
              zIndex: 1300,
            }}
            PaperProps={{
              sx: {
                backgroundColor: 'rgba(30, 41, 59, 0.95)',
                backdropFilter: 'blur(20px)',
                border: '1px solid rgba(255, 255, 255, 0.1)',
                boxShadow: '0 8px 32px 0 rgba(0, 0, 0, 0.37)',
                zIndex: 1300,
              }
            }}
          >
            <DialogTitle sx={{ fontWeight: 'bold', color: 'rgba(255, 255, 255, 0.9)', pb: 1 }}>
              Cancel Interview
              {selectedInterview && (
                <Typography variant="subtitle2" sx={{ mt: 1, color: 'rgba(255, 255, 255, 0.7)' }}>
                  {selectedInterview.counsellor.firstName} {selectedInterview.counsellor.lastName}
                </Typography>
              )}
            </DialogTitle>
            <DialogContent>
              <Box sx={{ mt: 2 }}>
                <Typography variant="subtitle2" sx={{ mb: 1, color: 'rgba(255, 255, 255, 0.9)', fontWeight: 500 }}>
                  Reason for Cancellation
                </Typography>
                <TextField
                  fullWidth
                  multiline
                  rows={3}
                  value={cancelReason}
                  onChange={(e) => setCancelReason(e.target.value)}
                  placeholder="Enter reason for cancellation"
                  variant="outlined"
                  sx={{
                    '& .MuiInputBase-input': { color: 'white' },
                    '& .MuiInputLabel-root': { color: 'rgba(255, 255, 255, 0.7)' },
                    '& .MuiOutlinedInput-root': {
                      '& fieldset': { borderColor: 'rgba(255, 255, 255, 0.3)' },
                      '&:hover fieldset': { borderColor: 'rgba(255, 255, 255, 0.5)' }
                    }
                  }}
                />
              </Box>
            </DialogContent>
            <DialogActions sx={{ p: 3, pt: 1 }}>
              <Button 
                onClick={() => setOpenCancelDialog(false)} 
                sx={{ 
                  color: 'rgba(255, 255, 255, 0.9)',
                  borderColor: 'rgba(255, 255, 255, 0.3)',
                  '&:hover': {
                    borderColor: 'rgba(255, 255, 255, 0.5)',
                    backgroundColor: 'rgba(255, 255, 255, 0.05)'
                  }
                }}
              >
                Back
              </Button>
              <Button
                onClick={handleCancel}
                variant="contained"
                disabled={!cancelReason}
                sx={{
                  backgroundColor: 'rgba(239, 68, 68, 0.2)',
                  color: 'rgba(255, 255, 255, 0.9)',
                  border: '1px solid rgba(239, 68, 68, 0.3)',
                  '&:hover': {
                    backgroundColor: 'rgba(239, 68, 68, 0.3)',
                    borderColor: 'rgba(239, 68, 68, 0.5)'
                  },
                  '&:disabled': {
                    backgroundColor: 'rgba(255, 255, 255, 0.1)',
                    color: 'rgba(255, 255, 255, 0.3)',
                    borderColor: 'rgba(255, 255, 255, 0.1)'
                  }
                }}
              >
                Cancel Interview
              </Button>
            </DialogActions>
          </Dialog>
    </>
  );
};

export default InterviewManagement;
